import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { themeTokens, resetStyles } from '../../styles/tokens.css.js';
import { STEP_ORDER, getStepIndex, type BookingStep } from '../../state/booking-store.js';

/** SVG icon paths for each step */
const STEP_ICONS: Record<BookingStep, string> = {
  service:
    // people/group icon
    'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z',
  datetime:
    // calendar icon
    'M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z',
  contact:
    // person icon
    'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z',
  payment:
    // credit card icon
    'M20 4H4c-1.11 0-2 .89-2 2v12c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.11-.9-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z',
  confirm:
    // checkmark circle icon
    'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
};

const STEP_LABELS: Record<BookingStep, string> = {
  service: "Who's coming?",
  datetime: 'Date & Time',
  contact: 'Your Information',
  payment: 'Payments',
  confirm: 'Confirmation',
};

@customElement('yebo-sidebar-nav')
export class YeboSidebarNav extends LitElement {
  static styles = [
    themeTokens,
    resetStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        background: var(--yebo-sidebar-bg);
        padding: var(--yebo-space-md);
        min-width: 200px;
        border-radius: var(--yebo-radius) 0 0 var(--yebo-radius);
        justify-content: space-between;
      }

      .steps {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .step-item {
        display: flex;
        align-items: center;
        gap: var(--yebo-space-sm);
        padding: 12px 14px;
        border-radius: var(--yebo-radius);
        cursor: default;
        transition: background var(--yebo-transition);
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-sidebar-text);
        line-height: 1.3;
        white-space: pre-line;
      }

      .step-item.active {
        background: var(--yebo-sidebar-active-bg);
        color: var(--yebo-sidebar-active-text);
      }

      .step-icon {
        flex-shrink: 0;
        width: 20px;
        height: 20px;
      }
      .step-item.active .step-icon {
        color: var(--yebo-sidebar-active-text);
      }

      .step-label {
        flex: 1;
        font-weight: 500;
      }

      .step-radio {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 2px solid currentColor;
        opacity: 0.5;
        position: relative;
      }
      .step-item.active .step-radio {
        opacity: 1;
        border-color: var(--yebo-primary);
      }
      .step-item.completed .step-radio {
        opacity: 1;
        border-color: var(--yebo-primary);
        background: var(--yebo-primary);
      }
      .step-item.completed .step-radio::after {
        content: '';
        position: absolute;
        top: 3px;
        left: 3px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--yebo-sidebar-bg);
      }

      /* Footer */
      .footer {
        margin-top: var(--yebo-space-xl);
        padding-top: var(--yebo-space-md);
        text-align: center;
      }
      .footer-label {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-sidebar-muted);
      }
      .footer-phone {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-sidebar-text);
        font-weight: 500;
        margin-top: 2px;
      }

      /* ── Mobile: the sidebar becomes a slim progress header ─────────────
         The desktop vertical list used to render UNCHANGED on phones — a
         200px dark block stacked over the card, colliding with the close
         button (the 2026-08-05 Creation screenshots). On narrow screens the
         whole list is replaced by one quiet row: current step name, "n / N",
         and a thin progress underline in the venue accent. */
      .mheader {
        display: none;
      }

      @media (max-width: 540px) {
        :host {
          min-width: 0;
          padding: 0;
          border-radius: var(--yebo-radius) var(--yebo-radius) 0 0;
        }
        .steps,
        .footer {
          display: none;
        }
        .mheader {
          display: block;
          /* clear the dialog's floating close button (top-right) */
          padding: 14px 56px 0 16px;
        }
        .mheader-row {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;
          padding-bottom: 10px;
        }
        .mheader-label {
          font-size: var(--yebo-font-size-base);
          font-weight: 600;
          color: var(--yebo-sidebar-text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .mheader-count {
          font-size: var(--yebo-font-size-sm);
          color: var(--yebo-sidebar-muted);
          font-variant-numeric: tabular-nums;
          flex-shrink: 0;
        }
        .mheader-bar {
          height: 3px;
          background: color-mix(in srgb, var(--yebo-sidebar-text) 18%, transparent);
        }
        .mheader-fill {
          height: 100%;
          background: var(--yebo-primary);
          transition: width var(--yebo-transition);
        }
      }
    `,
  ];

  @property({ type: String })
  current: BookingStep = 'service';

  @property({ type: Boolean, attribute: 'hide-payment' })
  hidePayment = false;

  /** Hide the date/time step (ticketed events use the fixed event date). */
  @property({ type: Boolean, attribute: 'hide-datetime' })
  hideDatetime = false;

  @property({ type: String, attribute: 'phone-number' })
  phoneNumber = '';

  private get _visibleSteps(): BookingStep[] {
    const hidden = new Set<BookingStep>(['confirm']);
    if (this.hidePayment) hidden.add('payment');
    if (this.hideDatetime) hidden.add('datetime');
    return STEP_ORDER.filter(s => !hidden.has(s));
  }

  render() {
    const steps = this._visibleSteps;
    const currentIdx = getStepIndex(this.current);

    // Mobile header state: position of the current step within the VISIBLE
    // list. 'confirm' is never in the list — show it as the full bar.
    const pos = steps.indexOf(this.current);
    const mPos = pos === -1 ? steps.length : pos + 1;
    const mLabel = (STEP_LABELS[this.current] ?? '').replace('\n', ' ');
    const mPct = Math.round((mPos / steps.length) * 100);

    return html`
      <div class="mheader" aria-hidden="false">
        <div class="mheader-row">
          <span class="mheader-label">${mLabel}</span>
          <span class="mheader-count">${mPos} / ${steps.length}</span>
        </div>
        <div class="mheader-bar" role="progressbar"
          aria-valuemin="1" aria-valuemax=${steps.length} aria-valuenow=${mPos}
          aria-label="Booking progress">
          <div class="mheader-fill" style="width: ${mPct}%"></div>
        </div>
      </div>
      <div class="steps">
        ${steps.map(step => {
          const idx = getStepIndex(step);
          const active = step === this.current;
          const completed = idx < currentIdx;
          return html`
            <div class="step-item ${active ? 'active' : ''} ${completed ? 'completed' : ''}">
              <svg class="step-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="${STEP_ICONS[step]}"/>
              </svg>
              <span class="step-label">${STEP_LABELS[step]}</span>
              <span class="step-radio"></span>
            </div>
          `;
        })}
      </div>

      ${this.phoneNumber ? html`
        <div class="footer">
          <div class="footer-label">Get in Touch</div>
          <div class="footer-phone">${this.phoneNumber}</div>
        </div>
      ` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'yebo-sidebar-nav': YeboSidebarNav;
  }
}
